import React from "react";
import { FormControl, FormHelperText, TextField, Typography } from "@mui/material";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Select from "@components/Select/Select";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTerminalServiceDocumentUpload } from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import type {
  CloseBypassServiceRequestPayload,
  TerminalServiceDocument,
} from "@services/terminal-services/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const LANG_BASE = "terminal-services.close-bypass";
const TEST_ID_BASE = "close-bypass-service-drawer";
const REMARKS_MAX_CHARS = 500;
const DOCUMENT_TITLE_MAX_CHARS = 50;
const ALLOWED_ATTACHMENT_FORMATS = ["pdf", "jpg", "jpeg", "png"];
const ATTACHMENT_VALIDATION_MESSAGE = "Only jpg, png, pdf and max 10 mb";
const DOCUMENT_TITLE_FIELD_NAME = "documentTitle";
const ISSUE_DATE_FIELD_NAME = "issueDate";
const EXPIRATION_DATE_FIELD_NAME = "expirationDate";

type CloseBypassServiceFormData = Record<string, string | File[] | Date | null>;

type CloseBypassServiceDrawerProps = {
  isOpen: boolean;
  requestId?: string | null;
  onClose: () => void;
  onCloseService: (payload: CloseBypassServiceRequestPayload) => void;
  isSubmitting?: boolean;
};

const DOCUMENT_TYPE_OPTIONS = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "VESSEL_MASTER_SOF",
  "OTHERS",
] as const;

const toAllowedExtensions = (extensions: string[]) =>
  extensions.map((extension) =>
    extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
  );

const getUploadedDocumentBase = (entry?: UploadEntry): TerminalServiceDocument | null => {
  if (!entry || entry instanceof File || !("url" in entry) || typeof entry.url !== "string") {
    return null;
  }

  return {
    documentUrl: entry.url,
    documentName: typeof entry.name === "string" ? entry.name : undefined,
  };
};

const formatDateField = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(String(value));

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const CloseBypassServiceDrawer = ({
  isOpen,
  requestId,
  onClose,
  onCloseService,
  isSubmitting = false,
}: CloseBypassServiceDrawerProps) => {
  const { t } = useTranslation();
  const storeKey = React.useMemo(
    () => `closeBypassServiceAttachment-${requestId ?? "unknown"}`,
    [requestId],
  );
  const attachmentForm = useForm<CloseBypassServiceFormData>({
    defaultValues: {
      [storeKey]: [],
      [DOCUMENT_TITLE_FIELD_NAME]: "",
      [ISSUE_DATE_FIELD_NAME]: null,
      [EXPIRATION_DATE_FIELD_NAME]: null,
    },
  });
  const uploadProfile = useFileUploadProfiles().additionalDocuments;
  const uploadConditions = {
    ...uploadProfile,
    format: ALLOWED_ATTACHMENT_FORMATS,
    hideErrors: true,
  };
  const { uploadEntries, setUploadEntries, handleUpload, resetUpload, isUploading } =
    useTerminalServiceDocumentUpload({
      storeKey,
      certificateType: "BYPASS_SERVICE_CLOSURE_ATTACHMENT",
      fallbackFileNamePrefix: "bypass-closure",
      errorNotification: {
        titleKey: `${LANG_BASE}.notifications.upload-error-title`,
        descriptionKey: `${LANG_BASE}.notifications.upload-error-description`,
      },
    });
  const [remarks, setRemarks] = React.useState("");
  const [documentType, setDocumentType] = React.useState("");
  const [otherDocumentType, setOtherDocumentType] = React.useState("");
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  const hasAttachment = Boolean(getUploadedDocumentBase(uploadEntries[0]));

  const resetDrawerData = React.useCallback(() => {
    setRemarks("");
    setDocumentType("");
    setOtherDocumentType("");
    setErrors({});
    setShowConfirmDialog(false);
    attachmentForm.reset({
      [storeKey]: [],
      [DOCUMENT_TITLE_FIELD_NAME]: "",
      [ISSUE_DATE_FIELD_NAME]: null,
      [EXPIRATION_DATE_FIELD_NAME]: null,
    });
    resetUpload();
  }, [attachmentForm, resetUpload, storeKey]);

  const handleCancel = React.useCallback(() => {
    resetDrawerData();
    onClose();
  }, [onClose, resetDrawerData]);

  const removeAttachment = React.useCallback(() => {
    attachmentForm.setValue(storeKey, []);
    attachmentForm.clearErrors(storeKey);
    resetUpload();
    setDocumentType("");
    setOtherDocumentType("");
    attachmentForm.setValue(DOCUMENT_TITLE_FIELD_NAME, "");
    attachmentForm.setValue(ISSUE_DATE_FIELD_NAME, null);
    attachmentForm.setValue(EXPIRATION_DATE_FIELD_NAME, null);
    setErrors({});
  }, [attachmentForm, resetUpload, storeKey]);

  const buildPayload = React.useCallback((): CloseBypassServiceRequestPayload | null => {
    const nextErrors: Record<string, string> = {};
    const documentTitle = String(attachmentForm.getValues(DOCUMENT_TITLE_FIELD_NAME) ?? "");
    const issueDate = formatDateField(attachmentForm.getValues(ISSUE_DATE_FIELD_NAME));
    const expirationDate = formatDateField(attachmentForm.getValues(EXPIRATION_DATE_FIELD_NAME));

    if (remarks.trim().length > REMARKS_MAX_CHARS) {
      nextErrors.remarks = t("form-validation.max-length", {
        label: t(`${LANG_BASE}.fields.remarks`),
        max: REMARKS_MAX_CHARS,
      });
    }

    const attachmentBase = getUploadedDocumentBase(uploadEntries[0]);

    if (attachmentBase) {
      if (!documentType) {
        nextErrors.documentType = t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.document-type`),
        });
      }
      if (issueDate) {
        const selectedIssueDate = new Date(issueDate);
        const today = new Date();

        selectedIssueDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (selectedIssueDate > today) {
          nextErrors.issueDate = t("form-validation.must-not-be-in-future", {
            label: t(`${LANG_BASE}.fields.issue-date`),
          });
        }
      }
      if (expirationDate) {
        const selectedExpirationDate = new Date(expirationDate);
        const today = new Date();

        selectedExpirationDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (selectedExpirationDate < today) {
          nextErrors.expirationDate = t("form-validation.must-be-in-past", {
            label: t(`${LANG_BASE}.fields.expiration-date`),
          });
        }
      }

      if (documentType === "OTHERS" && !otherDocumentType.trim()) {
        nextErrors.otherDocumentType = t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.other-document-type`),
        });
      }

      if (!documentTitle.trim()) {
        nextErrors.documentTitle = t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.document-title`),
        });
      } else if (documentTitle.trim().length > DOCUMENT_TITLE_MAX_CHARS) {
        nextErrors.documentTitle = t("form-validation.max-length", {
          label: t(`${LANG_BASE}.fields.document-title`),
          max: DOCUMENT_TITLE_MAX_CHARS,
        });
      }

      if (documentType === "CERTIFICATE" && !expirationDate) {
        nextErrors.expirationDate = t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.expiration-date`),
        });
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return null;
    }

    return {
      remarks: remarks.trim(),
      attachment: attachmentBase
        ? {
            ...attachmentBase,
            documentType,
            otherDocumentType: documentType === "OTHERS" ? otherDocumentType.trim() : "",
            documentName: documentTitle.trim(),
            issueDate: issueDate || null,
            expirationDate: expirationDate || null,
          }
        : null,
    };
  }, [attachmentForm, documentType, otherDocumentType, remarks, t, uploadEntries]);

  const handleSubmit = React.useCallback(() => {
    if (!buildPayload()) {
      return;
    }

    setShowConfirmDialog(true);
  }, [buildPayload]);

  const handleConfirmClosure = React.useCallback(() => {
    const payload = buildPayload();

    if (!payload) {
      setShowConfirmDialog(false);
      return;
    }

    setShowConfirmDialog(false);
    onCloseService(payload);
  }, [buildPayload, onCloseService]);

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid={TEST_ID_BASE} className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.title`)}
        onClose={onClose}
        onCancel={handleCancel}
        submitLabel={t(`${LANG_BASE}.submit`)}
        cancelLabel={t("common.cancel")}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isUploading}
      >
        <div className="flex-1 w-[400px] max-w-[100vw] py-2">
          <div className="flex flex-col gap-4 px-6">
            <div className="flex flex-col gap-2">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.fields.remarks`)}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={remarks}
                error={Boolean(errors.remarks)}
                inputProps={{ maxLength: REMARKS_MAX_CHARS }}
                onChange={(event) => setRemarks(event.target.value)}
                data-testid={`${TEST_ID_BASE}-remarks`}
              />
              {errors.remarks && (
                <FormHelperText error sx={{ m: 0 }} data-testid={`${TEST_ID_BASE}-remarks-error`}>
                  {errors.remarks}
                </FormHelperText>
              )}
              <FormHelperText sx={{ ml: 0, mt: 0.5, fontSize: 12, textAlign: "right" }}>
                {remarks.length}/{REMARKS_MAX_CHARS}
              </FormHelperText>
            </div>

            <FormProvider {...attachmentForm}>
              <FormItem<CloseBypassServiceFormData>
                name={storeKey}
                type="file"
                maxFiles={1}
                label={t(`${LANG_BASE}.fields.attachment`)}
                testId={`${TEST_ID_BASE}-attachment-upload`}
                uploadConditions={uploadConditions}
                allowedFileExtensions={toAllowedExtensions(ALLOWED_ATTACHMENT_FORMATS)}
                uploadDescription={ATTACHMENT_VALIDATION_MESSAGE}
                files={uploadEntries}
                setFiles={setUploadEntries}
                onUpload={(_, files) => {
                  attachmentForm.clearErrors(storeKey);
                  handleUpload(files);
                }}
                onReject={() =>
                  attachmentForm.setError(storeKey, {
                    type: "manual",
                    message: ATTACHMENT_VALIDATION_MESSAGE,
                  })
                }
                onRemoveFile={removeAttachment}
                disabled={isSubmitting || isUploading}
              />
              {uploadEntries[0] && (
                <SingleFile
                  testId={`${TEST_ID_BASE}-attachment`}
                  file={uploadEntries[0]}
                  fieldName={storeKey}
                  currentFileIndex={0}
                  setFiles={removeAttachment}
                  removeFn={removeAttachment}
                  isLoading={isUploading && uploadEntries[0] instanceof File}
                />
              )}
              {hasAttachment && (
                <>
                  <FormControl fullWidth error={Boolean(errors.documentType)}>
                    <CustomFormLabel sx={{ mb: 1 }}>
                      {t(`${LANG_BASE}.fields.document-type`)}
                    </CustomFormLabel>
                    <Select
                      testId={`${TEST_ID_BASE}-document-type`}
                      value={documentType}
                      error={Boolean(errors.documentType)}
                      placeholder={t(`${LANG_BASE}.fields.document-type-placeholder`)}
                      options={DOCUMENT_TYPE_OPTIONS.map((value) => ({
                        value,
                        label: t(`${LANG_BASE}.document-types.${value}`),
                        testId: `${TEST_ID_BASE}-document-type-${value}`,
                      }))}
                      onChange={(value) => {
                        setDocumentType(value);
                        setErrors((current) => ({ ...current, documentType: "" }));
                      }}
                    />
                    {errors.documentType && (
                      <FormHelperText sx={{ ml: 0 }}>{errors.documentType}</FormHelperText>
                    )}
                  </FormControl>

                  {documentType === "OTHERS" && (
                    <div className="flex flex-col gap-2">
                      <CustomFormLabel>
                        {t(`${LANG_BASE}.fields.other-document-type`)}
                      </CustomFormLabel>
                      <TextField
                        fullWidth
                        value={otherDocumentType}
                        error={Boolean(errors.otherDocumentType)}
                        helperText={errors.otherDocumentType}
                        inputProps={{ maxLength: 30 }}
                        onChange={(event) => setOtherDocumentType(event.target.value)}
                        data-testid={`${TEST_ID_BASE}-other-document-type`}
                      />
                    </div>
                  )}

                  <FormItem<CloseBypassServiceFormData>
                    name={DOCUMENT_TITLE_FIELD_NAME}
                    type="text"
                    label={t(`${LANG_BASE}.fields.document-title`)}
                    validationLabel={t(`${LANG_BASE}.fields.document-title`)}
                    testId={`${TEST_ID_BASE}-document-title`}
                    maxChars={DOCUMENT_TITLE_MAX_CHARS}
                    showErrorMsg={false}
                    slotProps={{ htmlInput: { maxLength: DOCUMENT_TITLE_MAX_CHARS } }}
                  />
                  {errors.documentTitle && (
                    <FormHelperText
                      error
                      sx={{ m: 0 }}
                      data-testid={`${TEST_ID_BASE}-document-title-error`}
                    >
                      {errors.documentTitle}
                    </FormHelperText>
                  )}

                  <FormItem<CloseBypassServiceFormData>
                    name={ISSUE_DATE_FIELD_NAME}
                    type="date-picker"
                    label={t(`${LANG_BASE}.fields.issue-date`)}
                    validationLabel={t(`${LANG_BASE}.fields.issue-date`)}
                    dateConstraint="not-future"
                    testId={`${TEST_ID_BASE}-issue-date`}
                    timePicker={false}
                    showErrorMsg={false}
                    forceHighZIndexPopper
                  />
                  {errors.issueDate && (
                    <FormHelperText
                      error
                      sx={{ m: 0 }}
                      data-testid={`${TEST_ID_BASE}-issue-date-error`}
                    >
                      {errors.issueDate}
                    </FormHelperText>
                  )}

                  <FormItem<CloseBypassServiceFormData>
                    name={EXPIRATION_DATE_FIELD_NAME}
                    type="date-picker"
                    label={t(`${LANG_BASE}.fields.expiration-date`)}
                    validationLabel={t(`${LANG_BASE}.fields.expiration-date`)}
                    testId={`${TEST_ID_BASE}-expiration-date`}
                    timePicker={false}
                    dateConstraint="not-past"
                    showErrorMsg={false}
                    forceHighZIndexPopper
                  />
                  {errors.expirationDate && (
                    <FormHelperText
                      error
                      sx={{ m: 0 }}
                      data-testid={`${TEST_ID_BASE}-expiration-date-error`}
                    >
                      {errors.expirationDate}
                    </FormHelperText>
                  )}
                </>
              )}
            </FormProvider>
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={t(`${LANG_BASE}.confirm-title`)}
        content={t(`${LANG_BASE}.confirm-message`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_BASE}.confirm-submit`)}
        handleCloseDialog={() => setShowConfirmDialog(false)}
        confirmAction={handleConfirmClosure}
        testId={`${TEST_ID_BASE}-confirm-dialog`}
        isInsideDrawer
      />
    </div>
  );
};

export default CloseBypassServiceDrawer;
